// Owner: account-settings (plan §9.3). The member's colour scheme on `<html data-theme>`: `light`
// or `dark` (`system` follows the operating system and changes with it). The preference is kept
// per browser too, so the first paint after a reload already has it. Dark tokens come from
// design-system (§9.1); this module only decides the attribute.
import type { ThemePreference } from '@dataroom/core';

const STORAGE_KEY = 'dr.theme';
let preference: ThemePreference = 'system';
let media: MediaQueryList | null = null;

function systemTheme(): 'light' | 'dark' {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function paint(): void {
  const root = document.documentElement;
  root.dataset.theme = preference === 'system' ? systemTheme() : preference;
  root.dataset.themePreference = preference;
}

function onSystemChange(): void {
  if (preference === 'system') paint();
}

/** Apply a theme preference now and remember it in this browser. */
export function applyTheme(next: ThemePreference): void {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private windows may refuse storage; the attribute still applies.
  }
  if (!media) {
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
      media.addEventListener('change', onSystemChange);
    } catch {
      media = null;
    }
  }
  paint();
}

/** The preference this browser remembers (before `/me` arrives). */
export function storedTheme(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // Storage unavailable: follow the system.
  }
  return 'system';
}
